import { IEvent } from "../interfaces/IEvent";
import { useSession } from '../SessionContext';
import { IUserSession } from '../interfaces/IUserSession';

const DRAFT_KEY_PREFIX = 'event-draft-new';

export interface EventDraft {
    formValues: IEvent;
    activeStep: number;
    completedSteps: number[];
    savedAt: string;
}

function getDraftKey(userId: string | undefined): string | null {
    if (!userId) return null;
    return `${DRAFT_KEY_PREFIX}-${userId}`;
}

const getDraftStorage = (): Storage | null => {
    if (typeof window === 'undefined') return null;
    return window.sessionStorage;
};

export function useEventDraft() {
    const { session } = useSession<IUserSession>();
    const userId = session?.user?.id;

    const loadDraft = (): EventDraft | null => {
        const key = getDraftKey(userId);
        if (!key) return null;

        const draftStorage = getDraftStorage();
        if (!draftStorage) return null;

        try {
            const raw = draftStorage.getItem(key);
            return raw ? (JSON.parse(raw) as EventDraft) : null;
        } catch {
            return null;
        }
    };

    const saveDraft = (formValues: IEvent, activeStep: number, completedSteps: number[]) => {
        const key = getDraftKey(userId);
        if (!key) return;

        const draftStorage = getDraftStorage();
        if (!draftStorage) return;

        try {
            draftStorage.setItem(key, JSON.stringify({
                formValues,
                activeStep,
                completedSteps,
                savedAt: new Date().toISOString(),
            }));
        } catch {
            // Storage quota exceeded or unavailable — silently ignore
        }
    };

    const discardDraft = () => {
        const key = getDraftKey(userId);
        if (!key) return;

        const draftStorage = getDraftStorage();
        if (!draftStorage) return;

        draftStorage.removeItem(key);
    };

    return { loadDraft, saveDraft, discardDraft };
}

export function clearAllEventDrafts() {
    const draftStorage = getDraftStorage();
    if (!draftStorage) return;

    const keysToRemove: string[] = [];
    for (let i = 0; i < draftStorage.length; i++) {
        const key = draftStorage.key(i);
        if (key && key.startsWith(DRAFT_KEY_PREFIX)) {
            keysToRemove.push(key);
        }
    }
    keysToRemove.forEach(key => draftStorage.removeItem(key));
}
